import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Lightbulb, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const TIPS = {
  dashboard: "Your shift starts here — stock alerts, overdue tools and today's jobs are all on this page.",
  inventory: "Add items with their country, currency and cost, then use + and − on a row to book material in or out.",
  tools: "Check a tool out to a person, check it back in when it returns, and calibration due dates are flagged for you.",
  partners: "Keep your suppliers and customers here so they can be picked on requests, orders and shipments.",
  requests: "Ask suppliers for a price, compare what comes back, then turn the best one into a purchase order.",
  orders: "Raise a purchase order, track it through to delivery, and receipt the goods against your stock.",
  shipping: "Pick the countries, weight and distance, compare every carrier on the lane, then book and pay by card.",
  logistics: "Your fleet, drivers and depots live here — build the dispatch board and assign trips to runs.",
  insurance: "Cover a shipment or the yard, hold your certificates, and raise a claim with photos.",
  finance: "Compare lenders and products, apply once, and repay online when an offer is accepted.",
  marketplace: "Advertise stock or put work out to tender, then award the best bid and take payment.",
  payments: "Send a payment link, take card payments and download the receipt as a PDF.",
  network: "Find other workshops and trades, post announcements and message people directly.",
  "live-feed": "Post photos, videos and on-shift updates — mark a post external to share it outside the workspace.",
  jobs: "Post a vacancy, shortlist applicants, book interviews and run apprenticeships and training.",
  advocates: "Ask a verified advisor a question, get a fixed fee quote and pay only when you accept it.",
  services: "Type your postal or PIN code to find accountants, engineers and labs nearest to your site.",
  privacy: "Turn on two-step sign-in, review your devices and export or delete your data at any time.",
  ai: "Ask the copilot anything about your operation, or let it write adverts, quotes and job posts for you.",
  safety: "Read the fire, flood and earthquake procedure for your site, report an incident, and keep the extinguisher and drill records straight.",
  tariffs: "Set the import duty and VAT for each product and trade lane, then work out what a shipment really costs once duty, freight and tax land.",
  parcels: "Compare the post office against the couriers for documents and parcels, on weight bands and volumetric weight, then add the tracking number to the shipment.",
};

const ROUTE_MODULES = {
  "/": "dashboard", "/inventory": "inventory", "/tools": "tools", "/partners": "partners",
  "/requests": "requests", "/orders": "orders", "/shipping": "shipping", "/logistics": "logistics",
  "/insurance": "insurance", "/finance": "finance", "/marketplace": "marketplace",
  "/payments": "payments", "/network": "network", "/live-feed": "live-feed", "/jobs": "jobs",
  "/advocates": "advocates", "/services": "services", "/privacy": "privacy", "/ai": "ai",
  "/safety": "safety",
  "/tariffs": "tariffs",
  "/parcels": "parcels",
};

export const GuidedTip = () => {
  const { pathname } = useLocation();
  const [seen, setSeen] = useState(null);
  const module = ROUTE_MODULES[pathname];

  useEffect(() => {
    api.get("/support/tips/seen").then(({ data }) => setSeen(data.seen || [])).catch(() => setSeen([]));
  }, []);

  if (!module || !TIPS[module] || !seen || seen.includes(module)) return null;

  const dismiss = () => {
    setSeen((s) => [...s, module]);
    api.post(`/support/tips/${module}/seen`).catch(() => {});
  };

  return (
    <div
      data-testid={`guided-tip-${module}`}
      className="mb-5 flex items-start gap-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] p-4 transition-opacity duration-200"
    >
      <Lightbulb size={18} className="mt-0.5 shrink-0 text-[#002FA7]" />
      <div className="min-w-0 flex-1">
        <div className="overline text-[0.6rem] text-[#002FA7]">First time here</div>
        <p className="mt-1 text-sm text-[#0F172A]">{TIPS[module]}</p>
        <Link to="/help" data-testid="guided-tip-help-link" className="tap-safe mt-2 inline-block text-xs text-[#002FA7] hover:underline">
          More guides in Help &amp; Support
        </Link>
      </div>
      <button
        type="button"
        aria-label="Dismiss tip"
        data-testid={`guided-tip-dismiss-${module}`}
        onClick={dismiss}
        className="shrink-0 p-2 text-slate-500 transition-colors duration-150 hover:text-[#0F172A]"
      >
        <X size={15} />
      </button>
    </div>
  );
};
